import validateFieldForPlaceholder from 'lib/validateFieldPlaceholder';
import React from 'react';
import { Table } from 'reactstrap';

import RenderObjectRow from './RenderObjectRow';
import styles from './styles.module.css';
import { parseDate } from './utils';

/*
----------------------------------
RenderListTable Component
----------------------------------
This component renders the list table
Render columns dynamically if not passed
in the listConfig.

If the list doesn't have any child node then, pass the childNode as null in the listConfig e.g

{ name: 'Softwares', key: 'software_list', type: 'list', listConfig: { childNode: null, config: [
            { name: 'Name', key: 'product', type: 'string' },
            { name: 'Vendor', key: 'vendor', type: 'string' },
  ]}
},
----------------------------------
*/
const RenderListTable = ({ list, listConfig, name = '', nestedCall }) => {
  // if list is empty or null
  if (
    !list ||
    !Array.isArray(list) ||
    list.length === 0 ||
    Object.keys(list[0]).length === 0
  ) {
    return (
      <tr key={`list-${name}`}>
        <td>{name}</td>
        <td colSpan={2}>{validateFieldForPlaceholder(null)}</td>
      </tr>
    );
  }
  let fetchedListConfig;
  // if listConfig is not passed as a prop, then fetch it from the first item in the list
  if (!listConfig || !listConfig.config || listConfig.config.length === 0) {
    const firstItem = list[0]; // Assuming at least one item is present in the data array
    const childNode = Object.keys(firstItem)[0];
    const keys = Object.keys(firstItem[childNode]); // Get the keys from the first item's child node object
    fetchedListConfig = {
      childNode,
      config: [
        ...keys.map((key) => ({
          key,
          name: key.split(/(?=[A-Z])/).join(' '),
          type:
            listConfig?.config?.find((item) => item.key === key)?.type ||
            'string',
        })),
      ],
    };
  }
  // if listConfig is not passed as param, then use the fetchedListConfig
  const { childNode, config } =
    listConfig && listConfig?.config?.length ? listConfig : fetchedListConfig;
  // generate table headers
  const headers = config.map(({ key, name }) => (
    <th key={key}>
      <strong>
        {name && name.length ? name : key.split(/(?=[A-Z])/).join(' ')}
      </strong>
    </th>
  ));

  // generate table rows
  const rows = list.map((item, index) => {
    const rowData = config.map(({ key, type, config }) => {
      let value;
      if (childNode) {
        value = item[childNode][key];
      } else {
        value = item[key];
      }
      let formattedValue;
      switch (type) {
        case 'string':
          formattedValue = validateFieldForPlaceholder(value);
          break;
        case 'date':
          formattedValue = parseDate(value);
          break;
        case 'url':
          let formattedUrl = validateFieldForPlaceholder(value);
          if (formattedUrl.toLowerCase().includes('http')) {
            formattedValue = (
              <a
                href={
                  formattedUrl.toLowerCase().includes('http')
                    ? `${formattedUrl}`
                    : '#'
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                {formattedUrl}
              </a>
            );
          } else {
            formattedValue = formattedUrl;
          }
          break;
        default:
          formattedValue = validateFieldForPlaceholder(value);
      }

      if (
        typeof formattedValue === 'object' &&
        !React.isValidElement(formattedValue)
      ) {
        return (
          <RenderObjectRow data={formattedValue} label={key} config={config} />
        );
      }

      return formattedValue;
    });

    return (
      <tr key={index}>
        {rowData.map((value, idx) => (
          <td key={idx}>{value}</td>
        ))}
      </tr>
    );
  });

  return (
    <>
      {
        // if not nested call we will render with name
        name !== '' ? (
          <tr>
            <td>{name}</td>
            <td>
              <Table
                id="list-table"
                className={`tablesorter table-striped ${styles.fixedHorizontalScrollTable}`}
                responsive
              >
                <thead>
                  <tr>{headers}</tr>
                </thead>
                <tbody>{rows}</tbody>
              </Table>
            </td>
          </tr>
        ) : (
          // if nested call we will render without name
          <Table
            id="list-table"
            className="tablesorter table-scrollable table-striped"
            responsive
          >
            <thead>
              <tr>{headers}</tr>
            </thead>
            <tbody>{rows}</tbody>
          </Table>
        )
      }
    </>
  );
};

export default RenderListTable;
